import { createContext, useContext, useEffect, useMemo, useState, useRef, useCallback } from "react";
import * as signalR from "@microsoft/signalr";
import { toast } from "react-toastify";
import { useAuth } from "@/context/AuthContext";
import { fetchTraderProductList } from "@/services/products";
import { usePricesStore } from "@/store/prices";
import { useSocketStatusStore } from "@/store/socketStatus";
import usePendingOrdersStore from "@/store/pendingOrders";
import { playDingSound, playRejectionSound } from "@/utils/sound";
import { getConfig } from "@/utils/config";
import { clearAuthSession, isUnauthorizedError } from "@/utils/auth";

/** Custom event name for order status updates (triggers history refresh) */
export const ORDER_STATUS_UPDATED_EVENT = "orderStatusUpdated";
export const AUCTION_UPDATED_EVENT = "auctionUpdated";
export const LAFZ_UPDATED_EVENT = "lafzUpdated";

const RateContext = createContext(null);

/**
 * Get HUB URL from runtime config
 * Derives hub URL from API base URL if VITE_HUB_URL is not set
 */
function getHubUrl() {
  const hubUrl = getConfig('VITE_HUB_URL', '');
  if (hubUrl) {
    return hubUrl;
  }
  
  // Derive hub URL from API base URL if VITE_HUB_URL is not set
  const apiBaseUrl = getConfig('VITE_API_BASE_URL', '');
  if (apiBaseUrl) {
    // Replace /api/v1 with /hub/trading
    return apiBaseUrl.replace('/api/v1', '/hub/trading');
  }
  
  // Fallback (should not be used if config is properly set)
  console.warn('[RateContext] VITE_HUB_URL and VITE_API_BASE_URL are not set. Please configure config.json.');
  return '';
}

const EVENT_NAME = "TradingProductRateList";
const TOKEN_KEY = "auth_token";

// Exponential backoff delays: 0s, 2s, 10s, 30s
const RECONNECT_DELAYS = [0, 2000, 10000, 30000];

export function RateProvider({ children }) {
  const [resetRejectTime, setResetRejectTime] = useState(0);
  const bumpRejectCooldown = useCallback(() => {
    setResetRejectTime((n) => n + 1);
  }, []);
  const { isAuthenticated } = useAuth();
  const [tradingProductRateData, setTradingProductRateData] = useState(null);
  const [productsList, setProductsList] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState("Disconnected");
  const [isConnected, setIsConnected] = useState(false);
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const updatePricesStore = usePricesStore((s) => s.updateFromRateContext);
  const setSocketConnected = useSocketStatusStore((s) => s.setConnected);
  const setSocketConnecting = useSocketStatusStore((s) => s.setConnecting);

  const connectionRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const isMountedRef = useRef(true);
  const productsListRef = useRef([]);
  const reloadProductsRef = useRef(null);

  // Keep productsListRef in sync with productsList
  useEffect(() => {
    productsListRef.current = productsList;
  }, [productsList]);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (connectionRef.current) {
      connectionRef.current
        .stop()
        .then(() => {
          if (isMountedRef.current) {
            setConnectionStatus("Disconnected");
            setIsConnected(false);
            setSocketConnected(false);
          }
        })
        .catch((error) => {
          console.warn("[RateContext] Error stopping connection:", error);
        });
      connectionRef.current = null;
    }
  }, []);

  // Start connection
  const startConnection = useCallback(async () => {
    // Cleanup existing connection
    cleanup();

    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      console.warn("[RateContext] No token found in localStorage");
      if (isMountedRef.current) {
        setConnectionStatus("Disconnected");
        setIsConnected(false);
        setSocketConnected(false);
      }
      return;
    }

    try {
      if (isMountedRef.current) {
        setConnectionStatus("Connecting...");
        setIsConnected(false);
        setSocketConnecting(true);
        setSocketConnected(false);
      }

      // Create connection
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(getHubUrl(), {
          transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling,
          accessTokenFactory: () => token,
        })
        .configureLogging(signalR.LogLevel.Information)
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            const attempt = retryContext.previousRetryCount;
            if (attempt < RECONNECT_DELAYS.length) {
              return RECONNECT_DELAYS[attempt];
            }
            // After all delays, use the last delay (30s) for subsequent attempts
            return RECONNECT_DELAYS[RECONNECT_DELAYS.length - 1];
          },
        })
        .build();

      connectionRef.current = connection;

      // Handle connection events (moved to after event listener registration)

      connection.onreconnecting((error) => {
        if (isMountedRef.current) {
          setConnectionStatus("Connecting...");
          setIsConnected(false);
          setSocketConnecting(true);
          setSocketConnected(false);
        }
        if (error) {
          console.warn("[RateContext] Reconnecting due to error:", error);
        }
      });

      connection.onreconnected((connectionId) => {
        if (isMountedRef.current) {
          setConnectionStatus("Connected");
          setIsConnected(true);
          setSocketConnected(true);
          reconnectAttemptRef.current = 0;
        }
        if (reloadProductsRef.current) {
          void reloadProductsRef.current();
        }
        console.log("[RateContext] Reconnected. Connection ID:", connectionId);
      });

      // Listen to TradingProductRateList event
      connection.on(EVENT_NAME, (data) => {
        console.log("[RateContext] Received TradingProductRateList event:", data);
        if (isMountedRef.current) {
          const processedData = Array.isArray(data) ? data : [data];
          console.log("[RateContext] Raw SignalR data:", processedData);

          // Merge SignalR rate data with products list
          mergeRateDataWithProducts(processedData);
          setIsLoadingRates(false);
        }
      });

      // Helper: process rate/update payload (used by both SendProductRate and SendProductUpdate)
      const processProductUpdates = (eventName, data) => {
        if (!isMountedRef.current) return;
        let updates = data;
        while (Array.isArray(updates) && updates.length === 1 && Array.isArray(updates[0])) {
          updates = updates[0];
        }
        if (!Array.isArray(updates)) updates = [updates];
        // console.log(`[RateContext] Processing ${eventName} updates:`, updates);

        setTradingProductRateData((currentData) => {
          const dataToUpdate = (currentData && Array.isArray(currentData) && currentData.length > 0)
            ? currentData
            : productsListRef.current;
          if (!dataToUpdate || !Array.isArray(dataToUpdate) || dataToUpdate.length === 0) {
            console.log(`[RateContext] No data to update in ${eventName}. currentData:`, currentData, "productsListRef:", productsListRef.current);
            return currentData;
          }

          const idsMatch = (left, right) =>
            left !== undefined &&
            right !== undefined &&
            String(left) === String(right);

          const priceRateMatches = (update, product) => {
            const priceUIds = [update.buyPrice?.uId, update.sellPrice?.uId].filter(
              (uId) => uId !== undefined && uId !== null && uId !== ""
            );

            if (!product.rateId || priceUIds.length === 0) {
              return true;
            }

            return priceUIds.every((uId) => idsMatch(uId, product.rateId));
          };

          const updatedProducts = dataToUpdate.map((product) => {
            const update = updates.find((u) => {
              if (u.tradingProductId !== undefined) {
                return idsMatch(u.tradingProductId, product.id) && priceRateMatches(u, product);
              }

              const legacyProductMatch =
                // idsMatch(u.id, product.id) ||
                // idsMatch(u.tradingProductId, product.tradingProductId) ||
                idsMatch(u.tradingProductId, product.id) 
                // ||
                // idsMatch(u.productId, product.productId);

              return legacyProductMatch && priceRateMatches(u, product);
            });

            if (!update) return product;

            const newBuyPrice = update.buyPrice !== undefined
              ? (typeof update.buyPrice === 'object' && update.buyPrice !== null ? update.buyPrice : { uId: update.buyPrice?.uId || "1", value: update.buyPrice?.value ?? update.buyPrice ?? 0 })
              : product.buyPrice;
            const newSellPrice = update.sellPrice !== undefined
              ? (typeof update.sellPrice === 'object' && update.sellPrice !== null ? update.sellPrice : { uId: update.sellPrice?.uId || "1", value: update.sellPrice?.value ?? update.sellPrice ?? 0 })
              : product.sellPrice;

            // Normalize buyIsEnable/sellIsEnable (support boolean or string "true"/"false")
            const toBool = (v) => (v === true || v === "true");
            const newBuyIsEnable = update.buyIsEnable !== undefined ? toBool(update.buyIsEnable) : product.buyIsEnable;
            const newSellIsEnable = update.sellIsEnable !== undefined ? toBool(update.sellIsEnable) : product.sellIsEnable;

            const updated = {
              ...product, 
              buyPrice: newBuyPrice,
              sellPrice: newSellPrice,
              buyIsEnable: newBuyIsEnable,
              sellIsEnable: newSellIsEnable,
              isEnable: update.isEnable !== undefined ? toBool(update.isEnable) : product.isEnable,
              rateCurrentValue: update.rateCurrentValue !== undefined ? update.rateCurrentValue : product.rateCurrentValue,
              price: update.rateCurrentValue !== undefined ? update.rateCurrentValue : product.price,
              lastUpdate: new Date().toISOString(),
            };

            return updated;
          });

          if (updatedProducts && updatedProducts.length > 0) {
            setTimeout(() => updatePricesStore(updatedProducts), 0);
          }
          return updatedProducts;
        });
      };

      // Listen to SendProductRate event (single product rate update)
      connection.on("SendProductRate", (data) => {
        // console.log("[RateContext] Received SendProductRate event:", data);
        processProductUpdates("SendProductRate", data);
      });

      // Listen to SendProductUpdate event (product update with prices and enable flags)
      connection.on("SendProductUpdate", (data) => {
        // console.log("[RateContext] Received SendProductUpdate event:", data);
        processProductUpdates("SendProductUpdate", data);
        if (reloadProductsRef.current) {
          void reloadProductsRef.current();
        }
      });

      // Listen to SendOrderStatus: admin approved/rejected an order — refresh history and show toast
      connection.on("SendOrderStatus", (statusOrFirstArg) => {
        const status = Array.isArray(statusOrFirstArg) ? statusOrFirstArg[0] : statusOrFirstArg;
        // console.log("[RateContext] Received SendOrderStatus:", status);
        if (!isMountedRef.current) return;
        // 2 = approved, 3 = rejected (matches History.jsx statusMap)
        if (status === 2) {
          bumpRejectCooldown();
          usePendingOrdersStore.getState().clearAllPendingOrders();
          // Play notification sound when order is approved by admin
          playDingSound();
          setTimeout(() => {
            toast.dismiss();
            toast.info("سفارش‌ شما توسط ادمین تایید شد.");
          }, 1000);
        } else if (status === 3) {
          bumpRejectCooldown();
          usePendingOrdersStore.getState().clearAllPendingOrders();
          // Play rejection sound when order is rejected by admin
          playRejectionSound();
          setTimeout(() => {
            toast.dismiss();
            toast.info("سفارش‌ شما توسط ادمین رد شد.");
          }, 500);
        } else {
          toast.info("وضعیت یکی از سفارش‌های شما به‌روزرسانی شد. تاریخچه معاملات را ببینید.");
        }
        window.dispatchEvent(new CustomEvent(ORDER_STATUS_UPDATED_EVENT, { detail: { status } }));
      });

      connection.on("SendAuctionCreated", (auction) => {
        if (!isMountedRef.current) return;
        playDingSound();
        toast.info("یک فرصت ویژه جدید آمد");
        window.dispatchEvent(new CustomEvent(AUCTION_UPDATED_EVENT, { detail: { type: "created", auction } }));
      });

      connection.on("SendAuctionClosed", (auction) => {
        if (!isMountedRef.current) return;
        window.dispatchEvent(new CustomEvent(AUCTION_UPDATED_EVENT, { detail: { type: "closed", auction } }));
      });

      connection.on("SendLafzCreated", (lafz) => {
        if (!isMountedRef.current) return;
        playDingSound();
        toast.info("یک لفظ جدید ثبت شد");
        window.dispatchEvent(new CustomEvent(LAFZ_UPDATED_EVENT, { detail: { type: "created", lafz } }));
      });

      connection.on("SendLafzUpdated", (lafz) => {
        if (!isMountedRef.current) return;
        window.dispatchEvent(new CustomEvent(LAFZ_UPDATED_EVENT, { detail: { type: "updated", lafz } }));
      });

      connection.on("SendLafzClosed", (lafz) => {
        if (!isMountedRef.current) return;
        window.dispatchEvent(new CustomEvent(LAFZ_UPDATED_EVENT, { detail: { type: "closed", lafz } }));
      });

      // Add connection state change handler
      connection.onclose((error) => {
        console.log("[RateContext] Connection closed", error ? `with error: ${error.message}` : "normally");
        if (isMountedRef.current) {
          setConnectionStatus("Disconnected");
          setIsConnected(false);
          setSocketConnected(false);
        }
      });

      // Start connection
      console.log("[RateContext] Starting SignalR connection...");
      await connection.start();

      if (isMountedRef.current) {
        setConnectionStatus("Connected");
        setIsConnected(true);
        setSocketConnected(true);
        reconnectAttemptRef.current = 0;
      }

      console.log("[RateContext] Successfully connected to SignalR Hub. Connection ID:", connection.connectionId);
      console.log("[RateContext] Listening for events:", EVENT_NAME, ", SendProductRate, SendProductUpdate, SendOrderStatus, SendAuctionCreated, SendAuctionClosed");
    } catch (error) {
      console.error("[RateContext] Error starting connection:", error);

      if (isUnauthorizedError(error)) {
        // SignalR negotiate/start errors bypass axios interceptors, so force logout here.
        clearAuthSession({ redirectToLogin: true });
        cleanup();
        return;
      }

      if (isMountedRef.current) {
        setConnectionStatus("Disconnected");
        setIsConnected(false);
        setSocketConnected(false);
      }

      // Retry connection with exponential backoff
      const attempt = reconnectAttemptRef.current;
      if (attempt < RECONNECT_DELAYS.length) {
        const delay = RECONNECT_DELAYS[attempt];
        reconnectAttemptRef.current = attempt + 1;

        reconnectTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            startConnection();
          }
        }, delay);
      }
    }
  }, [cleanup, updatePricesStore, bumpRejectCooldown]);

  // Refresh rates function
  const refreshRates = useCallback(async () => {
    if (!connectionRef.current || !isConnected) {
      console.warn("[RateContext] Cannot refresh rates: not connected");
      return;
    }

    try {
      setIsLoadingRates(true);
      // If the hub has a method to request rates, call it here
      // For example: await connectionRef.current.invoke("GetTradingProductRateList");
      // Otherwise, the server should send the data automatically via the event
    } catch (error) {
      console.error("[RateContext] Error refreshing rates:", error);
      setIsLoadingRates(false);
    }
  }, [isConnected]);

  // Store latest rate data in ref to avoid dependency issues
  const latestRateDataRef = useRef(null);

  // Load products list from API
  const loadProductsList = useCallback(async () => {
    try {
      const products = await fetchTraderProductList();
      // console.log("[RateContext] Loaded products list:", products);
      if (isMountedRef.current) {
        setProductsList(products || []);
      }
    } catch (error) {
      // console.error("[RateContext] Error loading products list:", error);
    }
  }, []);

  useEffect(() => {
    reloadProductsRef.current = loadProductsList;
  }, [loadProductsList]);

  // Merge SignalR rate data with products list
  const mergeRateDataWithProducts = useCallback((rateData, productsParam) => {
    if (!rateData || !Array.isArray(rateData) || rateData.length === 0) {
      return;
    }

    // Store latest rate data
    latestRateDataRef.current = rateData;

    // Get current products list from state
    setProductsList((currentProducts) => {
      const productsToMerge = productsParam || currentProducts;

      if (!productsToMerge || productsToMerge.length === 0) {
        // If products list is not loaded yet, just store the rate data
        console.log("[RateContext] Products list not loaded yet, storing rate data");
        setTradingProductRateData(rateData);
        return currentProducts;
      }

      // Create a map of rateId/id to rate data for quick lookup
      // Support both rateId (from SignalR) and id (fallback)
      // Note: rateId will be added to products API response (traderproductlist) in the future
      // For now, we use product.id as fallback to match with SignalR rateId
      const rateMap = new Map();
      rateData.forEach((rate) => {
        if (rate.rateId) {
          rateMap.set(rate.rateId, rate);
        }
        // Also map by id if different from rateId (for fallback)
        if (rate.id && rate.id !== rate.rateId) {
          rateMap.set(rate.id, rate);
        }
      });

      // Merge products with rate data
      const mergedProducts = productsToMerge.map((product) => {
        // Priority: 1) product.rateId (if exists - will be added in future), 2) product.id (fallback)
        const productKey = product.rateId || product.id;
        const rate = productKey ? rateMap.get(productKey) : null;

        if (rate) {
          // Handle both old format (simple values) and new format (objects with uId and value)
          const rateBuyPrice = rate.buyPrice || rate.price || product.buyPrice;
          const rateSellPrice = rate.sellPrice || product.sellPrice;
          // If isEnable is false, disable both buy and sell
          const rateIsEnable = rate.isEnable !== undefined ? (rate.isEnable === true || rate.isEnable === "true") : true;
          const buyIsEnable = !rateIsEnable ? false : (rate.buyIsEnable !== undefined ? rate.buyIsEnable !== false : (product.buyIsEnable !== false));
          const sellIsEnable = !rateIsEnable ? false : (rate.sellIsEnable !== undefined ? rate.sellIsEnable !== false : (product.sellIsEnable !== false));

          return {
            ...product,
            buyPrice: typeof rateBuyPrice === 'object' && rateBuyPrice !== null
              ? rateBuyPrice
              : (product.buyPrice || { uId: "", value: rateBuyPrice || 0 }),
            sellPrice: typeof rateSellPrice === 'object' && rateSellPrice !== null
              ? rateSellPrice
              : (product.sellPrice || { uId: "", value: rateSellPrice || 0 }),
            price: rate.price || product.price,
            lastUpdate: rate.lastUpdate || product.lastUpdate,
            lastUpdateShamsi: rate.lastUpdateShamsi || product.lastUpdateShamsi,
            direction: rate.direction || product.direction,
            rateId: product.rateId || rate.rateId,
            isEnable: rate.isEnable !== undefined ? rateIsEnable : product.isEnable,
            buyIsEnable,
            sellIsEnable,
          };
        }

        return product;
      });

      console.log("[RateContext] Merged products with rate data:", mergedProducts);
      setTradingProductRateData(mergedProducts);
      return currentProducts; // Return unchanged to avoid re-render
    });
  }, []);

  // Set trading product rate data function
  const setTradingProductRateDataHandler = useCallback((data) => {
    setTradingProductRateData(data);
  }, []);

  // Load products list on mount and when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadProductsList();
    }
  }, [isAuthenticated, loadProductsList]);

  // Merge rate data when products list is loaded
  useEffect(() => {
    if (productsList.length === 0) {
      setTradingProductRateData([]);
      return;
    }

    if (latestRateDataRef.current?.length > 0) {
      // Re-merge whenever the list itself changes, not just its length.
      mergeRateDataWithProducts(latestRateDataRef.current, productsList);
      return;
    }

    // If no SignalR payload has arrived yet, use the fresh API list directly.
    setTradingProductRateData(productsList);
  }, [productsList, mergeRateDataWithProducts]);

  // Update prices store when tradingProductRateData changes
  useEffect(() => {
    if (tradingProductRateData && Array.isArray(tradingProductRateData)) {
      updatePricesStore(tradingProductRateData);
    }
  }, [tradingProductRateData, updatePricesStore]);

  // Sync SignalR connection status to socketStatusStore
  useEffect(() => {
    if (connectionStatus === "Connecting...") {
      setSocketConnecting(true);
      setSocketConnected(false);
    } else if (isConnected && connectionStatus === "Connected") {
      setSocketConnected(true);
    } else {
      setSocketConnected(false);
    }
  }, [isConnected, connectionStatus, setSocketConnected, setSocketConnecting]);

  // Initialize connection on mount and when authentication status changes
  useEffect(() => {
    isMountedRef.current = true;

    // Start connection if authenticated
    if (isAuthenticated) {
      // console.log("[RateContext] User authenticated, starting connection...");
      startConnection();
    } else {
      // console.log("[RateContext] User not authenticated, cleaning up connection...");
      cleanup();
    }

    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [isAuthenticated, startConnection, cleanup]);

  // Also listen for storage changes (for cross-tab synchronization and custom events)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === TOKEN_KEY) {
        const newToken = e.newValue;
        if (newToken && isAuthenticated) {
          // console.log("[RateContext] Token changed in storage, starting connection...");
          startConnection();
        } else if (!newToken) {
          cleanup();
        }
      }
    };

    // Listen for custom storage event (for same-tab changes)
    const handleCustomStorageChange = (e) => {
      if (e.detail?.key === TOKEN_KEY) {
        const newToken = e.detail.newValue;
        if (newToken && isAuthenticated) {
          // console.log("[RateContext] Token detected via custom event, starting connection...");
          startConnection();
        } else if (!newToken) {
          cleanup();
        }
      }
    };

    // Listen to storage events (cross-tab)
    window.addEventListener("storage", handleStorageChange);

    // Listen to custom event for same-tab changes
    window.addEventListener("localStorageChange", handleCustomStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("localStorageChange", handleCustomStorageChange);
    };
  }, [isAuthenticated, startConnection, cleanup]);

  const value = useMemo(
    () => ({
      tradingProductRateData,
      connectionStatus,
      isConnected,
      isLoadingRates,
      resetRejectTime,
      bumpRejectCooldown,
      refreshRates,
      setTradingProductRateData: setTradingProductRateDataHandler,
      reloadProducts: loadProductsList,
    }),
    [
      tradingProductRateData,
      connectionStatus,
      isConnected,
      isLoadingRates,
      resetRejectTime,
      bumpRejectCooldown,
      refreshRates,
      setTradingProductRateDataHandler,
      loadProductsList,
    ]
  );

  return (
    <RateContext.Provider value={value }>
      {children}
    </RateContext.Provider>
  );
}

export function useRate() {
  const ctx = useContext(RateContext);
  if (!ctx) {
    throw new Error("useRate must be used within RateProvider");
  }
  return ctx;
}
